/**
 * AI WORKSHOP AGENDA GENERATOR LOGIC
 * Tilpasser pedagogisk opplegg basert på skårer og risikoprofil.
 */

export const generateWorkshopAgenda = (input) => {
  const { theme, metrics, deptName, risks, strengths, teamSize, duration } = input;
  const { psi, cri, stress, leadership } = metrics;

  // Bestem kjernefokus basert på Agenda Logic
  let coreFocus = {
    title: theme,
    goal: `Styrke ${theme.toLowerCase()} i ${deptName} gjennom felles refleksjon og konkrete tiltak.`,
    primaryChallenge: risks[0] || "Generell forbedring av arbeidsmiljøet"
  };

  if (psi < 50) {
    coreFocus.logicFocus = "Psykologisk Trygghet & Ytringskultur";
    coreFocus.discussionPoints = [
      "Hva hindrer oss i å si ifra når vi er usikre?",
      "Hvordan reagerer vi som team når noen gjør en feil?",
      "Hva skal til for at alle stemmer blir hørt i møter?"
    ];
    coreFocus.exercise = "Trygghets-kartlegging: Identifisere 'soner for taushet' i hverdagen.";
  } else if (cri > 60) {
    coreFocus.logicFocus = "Kommunikasjon & Konflikthåndtering";
    coreFocus.discussionPoints = [
      "Hvor oppstår de vanligste misforståelsene hos oss?",
      "Hvordan skiller vi mellom saklig uenighet og personfokus?",
      "Hvilke uformelle kjøreregler har vi for uenighet?"
    ];
    coreFocus.exercise = "Perspektiv-bytte: Analyse av en fiktiv case basert på reelle gnisninger.";
  } else if (stress > 70) {
    coreFocus.logicFocus = "Arbeidsbelastning & Restitusjon";
    coreFocus.discussionPoints = [
      "Hvilke oppgaver gir energi, og hvilke tapper oss?",
      "Hvordan ser en god 'grensesetting' ut i vår arbeidshverdag?",
      "Hva er de største 'tidstyvene' vi kan eliminere?"
    ];
    coreFocus.exercise = "Prioriterings-matrisen: Hva skal vi stoppe, starte og fortsette med?";
  } else if (leadership < 55) {
    coreFocus.logicFocus = "Forventningsavklaring & Lederstøtte";
    coreFocus.discussionPoints = [
      "Hva trenger vi fra ledelsen for å prestere optimalt?",
      "Hvor er forventningene mest uklare i dag?",
      "Hvordan kan vi gi hverandre (og leder) bedre feedback?"
    ];
    coreFocus.exercise = "Forventnings-kontrakt: Definisjon av gjensidige behov mellom team og leder.";
  } else {
    coreFocus.logicFocus = "Kulturbygging & Styrking av Samarbeid";
    coreFocus.discussionPoints = [
      "Hva gjør oss stolte av å jobbe i dette teamet?",
      "Hvordan kan vi bruke våre styrker enda mer aktivt?",
      "Hva er vårt neste nivå som team?"
    ];
    coreFocus.exercise = "Styrke-workshop: Kartlegge teamets samlede superkrefter.";
  }

  return {
    meta: {
      title: `Strategisk Workshop: ${coreFocus.logicFocus}`,
      dept: deptName,
      duration: duration || "3 timer",
      teamSize: teamSize,
      goal: coreFocus.goal
    },
    agenda: [
      {
        step: 1,
        title: "Åpning og Formål",
        time: "15 min",
        desc: "Velkomst, presentasjon av dagens mål og etablering av trygge rammer for dialog.",
        facilitatorNote: "Vektlegg at dette er en læringsarena, ikke et evalueringsmøte."
      },
      {
        step: 2,
        title: "Presentasjon av Hovedfunn",
        time: "20 min",
        desc: `Gjennomgang av resultater fra kartleggingen med fokus på ${deptName}.`,
        facilitatorNote: `Fokuser på styrker (${strengths.slice(0,2).join(', ')}) før man adresserer risiko.`
      },
      {
        step: 3,
        title: "Refleksjonsøvelse (Individuell)",
        time: "20 min",
        desc: "Deltakerne reflekterer over egne opplevelser knyttet til resultatene.",
        prompt: "Hva kjenner du deg mest igjen i fra rapporten, og hva overrasker deg?"
      },
      {
        step: 4,
        title: "Fasilitert Gruppediskusjon",
        time: "45 min",
        desc: "Diskusjon i mindre grupper basert på prioriterte problemstillinger.",
        questions: coreFocus.discussionPoints
      },
      {
        step: 5,
        title: "Prioriteringsøvelse",
        time: "30 min",
        desc: coreFocus.exercise,
        facilitatorNote: "Sørg for at alle grupper lander på 2-3 konkrete områder."
      },
      {
        step: 6,
        title: "Tiltaksplanlegging (Action Plan)",
        time: "30 min",
        desc: "Konkretisering av tiltak: Hvem gjør hva, og når?",
        template: ["Tiltak beskrivelse", "Ansvarlig", "Frist", "Suksesskriterium"]
      },
      {
        step: 7,
        title: "Avslutning og Oppsummering",
        time: "15 min",
        desc: "Utsjekk og veien videre. Hvordan følger vi opp dette i hverdagen?",
        prompt: "Hva er det viktigste du tar med deg fra i dag?"
      }
    ],
    followUp: [
      "Gjennomgang av tiltaksliste i neste teammøte.",
      "1-til-1 samtaler med leder for å sikre støtte til tiltakene.",
      "Ny pulsmåling om 60 dager for å måle effekt."
    ]
  };
};

export const mockAgendaInput = {
  theme: "Teamutvikling",
  deptName: "Utviklingsavdelingen",
  metrics: {
    psi: 42,
    cri: 58,
    stress: 72,
    leadership: 48
  },
  risks: ["Taushetskultur ved feil", "Høyt arbeidspress", "Uklare forventninger"],
  strengths: ["Høy faglig kompetanse", "Godt samhold i kriser"],
  teamSize: 15,
  duration: "4 timer"
};